<template>
	<div class="summary_wrapper">
		<div class="content">
			<div class="summary_title">
				<h3>{{ $t("components.cart.order_summary") }}</h3>
				<div class="summary_total">
					<span class="summary_amount">$0,00<span class="dollar">USD</span></span>
				</div>
			</div>
			<div class="order_price">
				<div class="subtotal">
					<span class="label">{{ $t("components.cart.subtotal") }}</span>
					<span class="price">${{ getTotalPrice().toFixed(2) }} <span class="dollar">USD</span></span>
				</div>
				<div
					v-show="props.hasBoth"
					class="subtotal_products"
				>
					<!-- <span class="label">— 4 products / 4,000 meters</span> -->
					<span class="label">— {{ numOfProducts }} {{ numOfProducts > 1 ? "products" : "product" }}</span>
					<span class="price">${{ getProductsPrice().toFixed(2) }} <span class="dollar">USD</span></span>
				</div>
				<div
					v-show="props.hasBoth"
					class="subtotal_samples"
				>
					<span class="label">— {{ numOfSamples }} {{ numOfSamples > 1 ? "samples" : "sample" }}</span>
					<span class="price">${{ getSamplesPrice().toFixed(2) }} <span class="dollar">USD</span></span>
				</div>

				<div class="shipping">
					<span class="label">{{ $t("components.cart.shipping") }}</span>
					<span class="price">{{ $t("components.cart.calculated_next") }}</span>
				</div>
				<div class="tax">
					<span class="label">{{ $t("components.cart.taxes") }}</span>
					<span class="price">{{ $t("components.cart.calculated_next") }}</span>
				</div>
				<div class="total">
					<span class="label">{{ $t("components.cart.total") }}</span>
					<span class="price total_price">$<span class="dollar">USD</span></span>
				</div>
			</div>
			<Button
				color="primary"
				block
				size="large"
				class="my-[24px] tg-btn"
				@click="goToCheckout"
			>
				{{ $t("components.cart.proceed_to_checkout") }}
				<Icon name="md-chevron_right" />
			</Button>
			<div class="secured_powered">
				<div class="secured_payment">
					<Icon
						name="md-lock"
						:optical-size="20"
						class="secured_icon"
					/>
					<span class="ml-[6px] text-sm font-semibold">100% {{ $t("components.cart.secured_and_encrypted") }}</span>
				</div>
				<TengivaSupportedPayMethods class="flex justify-center" />
			</div>
			<div class="remark">
				<div class="need_help">
					<Icon
						name="md-info"
						class="support_icon"
					/>
					<div class="help_content">
						<span>{{ $t("components.cart.need_help_completing") }}</span>
						<a
							class="call_us tg-link-color"
							href="tel:+14389402400"
						>
							{{ $t("components.cart.call_us") }} +1 438-940-2400
						</a>
						<span class="time">{{ $t("components.cart.mon_to_fri") }}</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { useI18n } from "vue-i18n";
import type { Toast } from "@tengiva/components";
import { useGtag } from "vue-gtag-next";
import Button from "@/components/Button.vue";
import { mapCartToGAEvent } from "@/plugins/gtag.client";

const { $toast } = useNuxtApp();

const props = defineProps(["hasBoth"]);

const { getProductsPrice, getSamplesPrice, getTotalPrice } = useCartStore();
const { numOfProducts, numOfSamples, cartSamples, cartProductsGroupByWarehouse } = storeToRefs(useCartStore());
const { createCheckoutAuthorizationAuth } = useURMApi();
const { platformId } = storeToRefs(usePlatformStore());
const { cart } = useCartStore();
const { checkoutUrl } = useRuntimeConfig().public;
const gtag = useGtag();

const { t } = useI18n();

async function goToCheckout() {
	// no samples or number of smaples >= 5 can go to checkout
	if (numOfSamples.value >= 5 || numOfSamples.value === 0) {
		// 1. fetch Checkout auth token
		const { response } = await createCheckoutAuthorizationAuth({
			platform_id: platformId.value,
			cart_id: cart?.[0]?.id ?? (cart as any).id,
		});
		const checkoutToken = (response.value?.data as any).checkout_token;
		// 2. if has auth token, redirect to checkout page
		const fromUrl = window.location.host;
		if (checkoutToken) {
			if (cartProductsGroupByWarehouse.value && cartSamples.value) {
				const eventData = mapCartToGAEvent(cartProductsGroupByWarehouse.value, cartSamples.value);
				gtag.event("begin_checkout", eventData);
			}
			const langCode = ref<string>("en");
			const cached = localStorage.getItem("lang");
			if (cached) {
				langCode.value = JSON.parse(cached).code;
			}
			setTimeout(() => {
				sessionStorage.clear();
				window.location.href = `${checkoutUrl}?token=${checkoutToken}&platformId=${platformId.value}&fromUrl=${fromUrl}&lang=${langCode.value}`;
			});
		}
	} else {
		($toast as Toast)({
			message: t("pages.cart.a_minimum_of_5_samples"),
			type: "warning",
			location: "top right",
			closable: true,
			duration: 10000,
		});
	}
}
</script>
<style lang="scss" scoped>
.summary_wrapper {
	@apply border-l border-neutral-100  overflow-visible mobile:top-0;
	@apply pl-[40px] pt-[90px] max-w-[480px] min-w-[420px];
	@screen mobile {
		@apply border-0 border-y rounded-none static w-full shadow-none px-[15px];
		@apply py-[25px] mt-[25px];
	}

	.content {
		@apply sticky top-[150px];

		.summary_title {
			@apply flex items-center justify-between;
			h3 {
				@apply text-[32px] leading-[40px] font-medium text-neutral-900 mb-[24px];
			}

			.summary_total {
				@apply hidden;
				.summary_amount {
					@apply text-[16px] leading-[24px] text-neutral-900 font-bold;
					.dollar {
						@apply text-[14px] leading-[20px] font-normal ml-[2px];
					}
				}
			}
		}

		.order_price {
			@apply mb-[10px];
			div {
				@apply flex justify-between items-center text-[14px] text-neutral-900;

				.label {
					@apply text-neutral-700;
				}
			}

			.subtotal {
				@apply mb-[8px];
			}

			.subtotal,
			.subtotal_products,
			.subtotal_samples {
				.price {
					@apply text-[16px] leading-[24px] text-black;

					.dollar {
						@apply text-[12px] leading-[18px];
					}
				}
			}

			.subtotal_products {
				@apply mb-[4px];
			}

			.subtotal_products,
			.subtotal_samples {
				.price {
					@apply text-neutral-700;
				}
			}

			.shipping {
				@apply my-[8px];
				.info_icon {
					@apply text-[16px];
				}
			}

			.total {
				@apply h-[40px] border-t-[4px] border-neutral-100 pt-[10px] items-center mt-[14px];

				.total_price {
					@apply text-[20px] leading-[30px] font-medium;
				}

				.dollar {
					@apply text-[16px] leading-[24px] ml-[2px];
				}
			}
		}

		.secured_powered {
			@apply mb-[24px];

			.secured_payment {
				@apply text-[#039855] flex items-center justify-center mb-[8px] mobile:text-[14px];

				.secured_icon {
					@apply mobile:text-[18px];
				}
			}
		}

		.remark {
			@apply px-[16px] py-[12px] border border-neutral-100  mobile:px-[25px];
			@apply rounded border-l-[4px];
			div {
				@apply text-[14px] leading-[20px] text-neutral-900;
			}

			.need_help {
				@apply flex font-normal mt-[2px] space-x-[10px];

				.support_icon {
					@apply mobile:ml-[2.8%];
				}

				.help_content {
					@apply flex flex-col;

					.call_us {
						@apply underline cursor-pointer  font-medium;
					}

					.time {
						@apply text-neutral-700;
					}
				}
			}
		}
	}
}
</style>
